import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useCustomToast } from '@/shared/lib';
import { trainingMatrixApi } from '@/entities/Training';
import { useTrainingMatrixDocumentSections } from './useTrainingMatrixDocumentSections';
import { TRAINING_MATRIX_LISTING_ROUTE_NAME } from './useTrainingMatrixEditIndividual';

export interface TrainingMatrixBatchEditPosition {
  id: number;
  name: string;
}

/**
 * An empty `positionIds` means "edit the whole matrix" — the caller didn't scope the edit to a
 * selection, so every position in the branch is targeted instead of just a subset.
 */
export function useTrainingMatrixEditBatch(positionIds: number[]) {
  const toast = useCustomToast();
  const router = useRouter();
  const sections = useTrainingMatrixDocumentSections();
  const isEditingAll = positionIds.length === 0;

  const positions = ref<TrainingMatrixBatchEditPosition[]>([]);
  const loading = ref(true);
  const isSaving = ref(false);
  let resolvedPositionIds: number[] = positionIds;

  async function load(): Promise<void> {
    loading.value = true;
    try {
      const [current] = await Promise.all([
        trainingMatrixApi.getCurrent({ allItems: true }),
        sections.fetchAllSections(),
      ]);
      const allPositions = current.data.positions.map((position) => ({
        id: position.id,
        name: position.name,
      }));
      positions.value = isEditingAll
        ? allPositions
        : allPositions.filter((position) => positionIds.includes(position.id));
      if (isEditingAll) {
        resolvedPositionIds = allPositions.map((position) => position.id);
      }
    } finally {
      loading.value = false;
    }
  }

  function goToListing(): void {
    router.push({ name: TRAINING_MATRIX_LISTING_ROUTE_NAME });
  }

  async function save(): Promise<void> {
    if (isSaving.value) return;
    isSaving.value = true;
    try {
      await trainingMatrixApi.updateBatchEntries(
        resolvedPositionIds,
        sections.getSelectedDocumentRevisionIds()
      );
      toast.showSuccessToast({ title: 'Matriz atualizada com sucesso', type: 'success' });
      goToListing();
    } catch {
      toast.showErrorToast({
        title: 'Erro ao atualizar matriz. Tente novamente',
        type: 'error',
      });
    } finally {
      isSaving.value = false;
    }
  }

  function cancel(): void {
    goToListing();
  }

  load();

  return {
    ...sections,
    positions,
    loading,
    isSaving,
    save,
    cancel,
  };
}
